/**
 * Offline queue + drainer for the NEW soil application group lifecycle
 * (issue #417 Slice 4).
 *
 *   db.soilGroupStarts    -> POST /nutrition/soil-application-sessions/:id/groups/:groupId/start
 *   db.soilGroupCompletes -> POST /nutrition/soil-application-sessions/:id/groups/:groupId/complete
 *
 * Status machine:
 *   pending -> syncing -> synced | failed | quarantined (at 5 retries) | conflicted
 *
 * `conflicted` is terminal and specific to this slice: the backend answered 409
 * because it had already moved the group past this submission (spec §7 —
 * conflicts favour the backend). The item is never retried; instead the
 * worklist raises a dismissible banner naming the group, so an operator whose
 * capture was superseded is told rather than silently overwritten.
 *
 * This is a SEPARATE service from nutritionOperatorSessionSyncService, which
 * owns the foliar combo queues AND the legacy soil queues that POST to
 * /nutrition/soil-sessions/* — that file must not be touched.
 */
import axios from 'axios';
import { db } from '../db';
import type { SoilGroupStartQueueItem, SoilGroupCompleteQueueItem } from '../db';
import type { SoilGroupStartPayload, SoilGroupCompletePayload } from './soilGroupService';

const QUARANTINE_THRESHOLD = 5;
let inProgress = false;

export function __resetSoilGroupSyncFlagForTesting(): void { inProgress = false; }

const authHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const baseUrl = process.env.REACT_APP_API_URL || '/api/v1';
const groupUrl = (sessionId: string, groupId: string, action: 'start' | 'complete') =>
  `${baseUrl}/nutrition/soil-application-sessions/${sessionId}/groups/${groupId}/${action}`;

export async function enqueueSoilGroupStart(item: {
  sessionId: string; groupId: string; label: string; payload: SoilGroupStartPayload;
}): Promise<void> {
  await db.soilGroupStarts.add({
    ...item, syncStatus: 'pending', retryCount: 0, createdAt: new Date().toISOString(),
  } as SoilGroupStartQueueItem);
}

export async function enqueueSoilGroupComplete(item: {
  sessionId: string; groupId: string; label: string; payload: SoilGroupCompletePayload;
}): Promise<void> {
  await db.soilGroupCompletes.add({
    ...item, syncStatus: 'pending', retryCount: 0, createdAt: new Date().toISOString(),
  } as SoilGroupCompleteQueueItem);
}

/** Reset rows stuck in 'syncing' (crash/refresh) and 'quarantined' back to pending. */
export async function resetSoilGroupSyncState(): Promise<number> {
  let count = 0;
  count += await db.soilGroupStarts.where('syncStatus').equals('syncing').modify({ syncStatus: 'pending' });
  count += await db.soilGroupStarts.where('syncStatus').equals('quarantined').modify({ syncStatus: 'pending', retryCount: 0 });
  count += await db.soilGroupCompletes.where('syncStatus').equals('syncing').modify({ syncStatus: 'pending' });
  count += await db.soilGroupCompletes.where('syncStatus').equals('quarantined').modify({ syncStatus: 'pending', retryCount: 0 });
  return count;   // 'conflicted' is deliberately NOT reset — the backend won.
}

/**
 * Any 4xx EXCEPT 408 (Request Timeout) and 429 (Too Many Requests) is a
 * terminal rejection of the submission itself - the payload is invalid,
 * conflicts with server state, or the transition is not allowed - and will
 * never succeed no matter how many times it is retried. 408/429, 5xx, and
 * network errors (no `response` at all) are transient and keep the existing
 * retry/quarantine behaviour. 409 (CONFLICT) is one member of this broader
 * set; the loop above already special-cases it for the worklist banner.
 */
export const isTerminalRejection = (err: any) => {
  const status = err?.response?.status;
  return typeof status === 'number' && status >= 400 && status <= 499 && status !== 408 && status !== 429;
};

async function drain(
  table: typeof db.soilGroupStarts | typeof db.soilGroupCompletes,
  action: 'start' | 'complete',
): Promise<void> {
  const pending = await table.where('syncStatus').anyOf(['pending', 'failed']).toArray();
  if (pending.length > 0) {
    console.log(`[SoilGroupSync] Draining ${pending.length} queued ${action}(s)...`);
  }
  for (const item of pending as Array<SoilGroupStartQueueItem | SoilGroupCompleteQueueItem>) {
    try {
      await (table as any).update(item.localId!, { syncStatus: 'syncing' });
      await axios.post(groupUrl(item.sessionId, item.groupId, action), item.payload, { headers: authHeaders() });
      await (table as any).update(item.localId!, { syncStatus: 'synced' });
    } catch (err: any) {
      if (isTerminalRejection(err)) {
        // Terminal: a 409 means the backend already holds a newer truth for
        // this group; any other non-transient 4xx (422 UNIT_CONFLICT, 400
        // BAD_TIMES/INVALID_TRANSITION/SNAPSHOT_MISMATCH, ...) means the
        // submission itself can never succeed. Retrying it forever would
        // just quarantine it after 5 attempts with nothing ever surfaced -
        // mark it conflicted immediately, on the FIRST attempt, so the
        // worklist banner picks it up straight away.
        const status = err?.response?.status;
        console.warn(`[SoilGroupSync] ${action} item ${item.localId} terminally rejected (${status}) — backend state wins.`);
        await (table as any).update(item.localId!, {
          syncStatus: 'conflicted',
          lastError: err?.response?.data?.error || `rejected (${status})`,
        });
        continue;
      }
      const retryCount = (item.retryCount ?? 0) + 1;
      const syncStatus = retryCount >= QUARANTINE_THRESHOLD ? 'quarantined' : 'failed';
      console.warn(`[SoilGroupSync] ${action} item ${item.localId} ${syncStatus} (attempt ${retryCount}):`, err);
      await (table as any).update(item.localId!, { syncStatus, retryCount, lastError: String(err?.message || err) });
    }
  }
}

export async function syncPendingSoilGroups(): Promise<void> {
  if (inProgress) return;
  if (!navigator.onLine) return;
  inProgress = true;
  try {
    await drain(db.soilGroupStarts, 'start');
    await drain(db.soilGroupCompletes, 'complete');
  } finally {
    inProgress = false;
  }
}

/**
 * Group ids for this session with an offline transition still in flight.
 * Drives the detail screen's blue "queued" row modifier: a manager looking at a
 * row that has not reached the server yet must be told, not shown a stale
 * status. 'synced' and terminal states are excluded — only work still owed.
 */
export async function readPendingSoilGroupIds(sessionId: string): Promise<string[]> {
  const live = ['pending', 'syncing', 'failed'];
  const [starts, completes] = await Promise.all([
    db.soilGroupStarts.where('sessionId').equals(sessionId).toArray(),
    db.soilGroupCompletes.where('sessionId').equals(sessionId).toArray(),
  ]);
  const ids = starts.concat(completes as never[])
    .filter((r: { syncStatus: string }) => live.indexOf(r.syncStatus) !== -1)
    .map((r: { groupId: string }) => r.groupId);
  return Array.from(new Set(ids));
}

/** Queue rows still owing work. Shared by both cross-session readers below. */
const LIVE_SYNC_STATUSES = ['pending', 'syncing', 'failed'];

/**
 * Total live queued ACTIONS across every session — the number the worklist's
 * offline banner reports ("2 actions waiting to sync").
 *
 * Deliberately counts actions, not groups: a group with a queued start AND a
 * queued complete owes the server two writes, and reporting "1" would tell the
 * operator less work is outstanding than there is.
 */
export async function readPendingSoilGroupCount(): Promise<number> {
  const [starts, completes] = await Promise.all([
    db.soilGroupStarts.where('syncStatus').anyOf(LIVE_SYNC_STATUSES).count(),
    db.soilGroupCompletes.where('syncStatus').anyOf(LIVE_SYNC_STATUSES).count(),
  ]);
  return starts + completes;
}

/**
 * Distinct `${sessionId}:${groupId}` keys with any live queued action, across
 * every session. The key shape matches CachedSoilMyGroup.id exactly so the
 * worklist can test membership by row id with no re-derivation.
 */
export async function readPendingSoilGroupKeys(): Promise<string[]> {
  const [starts, completes] = await Promise.all([
    db.soilGroupStarts.where('syncStatus').anyOf(LIVE_SYNC_STATUSES).toArray(),
    db.soilGroupCompletes.where('syncStatus').anyOf(LIVE_SYNC_STATUSES).toArray(),
  ]);
  const keys = starts.concat(completes as never[])
    .map((r: { sessionId: string; groupId: string }) => `${r.sessionId}:${r.groupId}`);
  // es5 target: Array.from over the Set, never a spread.
  return Array.from(new Set(keys));
}

/** Conflicted completions, for the worklist banner. */
export async function readSoilGroupConflicts(): Promise<Array<{ localId: number; label: string; groupId: string }>> {
  const rows = await db.soilGroupCompletes.where('syncStatus').equals('conflicted').toArray();
  return rows.map((r) => ({ localId: r.localId!, label: r.label, groupId: r.groupId }));
}

export async function dismissSoilGroupConflict(localId: number): Promise<void> {
  await db.soilGroupCompletes.delete(localId);
}
